import React, { useMemo, useState } from 'react'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Text from 'components_new/atoms/Text'

import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { EconomySet } from 'types/GlobalEconomySets'
import { useKpiVariableOptions } from 'redux/hooks/KpiVariableOptions'
import { useTranslation } from 'language/translator'

interface UnusedAccountsAlertProps {
  economySet: EconomySet
}

/**
 * Warns when accounts in the chart of accounts are not
 * referenced by any account level, with a dialog listing
 * the unused accounts.
 */

const UnusedAccountsAlert = (props: UnusedAccountsAlertProps) => {
  const { economySet } = props

  const { t } = useTranslation()

  const [kpiVariableOptions] = useKpiVariableOptions()

  const [open, setOpen] = useState(false)

  const unusedAccounts = useMemo(() => {
    const usedAccounts = new Set(
      kpiVariableOptions
        .filter((option) => option.economy_set_id === economySet.id)
        .map((option) => option.selected)
        .flat()
    )

    return economySet.accounts
      .filter((account) => !usedAccounts.has(account.number))
      .sort(
        (aAccount, bAccount) =>
          parseInt(aAccount.number, 10) - parseInt(bAccount.number, 10)
      )
  }, [economySet, kpiVariableOptions])

  if (unusedAccounts.length === 0) {
    return null
  }

  return (
    <>
      <Alert
        action={
          <Button
            color="inherit"
            onClick={() => setOpen(true)}
            size="small"
            variant="text"
          >
            {t('global', 'show')}
          </Button>
        }
        severity="warning"
        sx={{ mb: 2, '& .MuiAlert-icon': { p: '6px 0' } }}
      >
        {t('accounting', '{{count}}_unused_accounts_warning', false, {
          count: unusedAccounts.length.toString()
        })}
      </Alert>

      {/* ----- Dialogs ----- */}
      <SimpleDialog
        actions={
          <Button onClick={() => setOpen(false)} variant="text">
            {t('global', 'close')}
          </Button>
        }
        contentText={t('accounting', 'unused_accounts_helper_text')}
        onClose={() => setOpen(false)}
        open={open}
        title={t('accounting', 'unused_accounts')}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {unusedAccounts.map((account) => (
            <Box
              key={account.id}
              sx={{ alignItems: 'center', display: 'flex', gap: 1 }}
            >
              <Chip label={account.number} size="small" />
              <Text variant="body2">{account.name}</Text>
            </Box>
          ))}
        </Box>
      </SimpleDialog>
    </>
  )
}

export default UnusedAccountsAlert
